import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useRef, useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  ANY_SPORT_MY_CLUB_SLUG,
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isType } from '@sorare/core/src/lib/gql';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from 'components/profile/Aside';
import { ClubLayout } from 'components/profile/ClubLayout';
import { CurrentFixture } from 'components/profile/CurrentFixture';
import { Gallery } from 'components/profile/Gallery';
import { ManagerIDHeader } from 'components/profile/ManagerIDHeader';
import { RecentPurchases } from 'components/profile/RecentPurchases';
import type { CampaignWinner } from 'components/profile/RepresentedBy';
import { RepresentedBy } from 'components/profile/RepresentedBy';
import { WatchlistsBlock } from 'components/profile/WatchlistsBlock';
import { Grid, Main, NewGrid } from 'components/profile/ui';

import type {
  ManagerProfileQuery,
  ManagerProfileQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const MANAGER_PROFILE_QUERY = gql`
  query ManagerProfileQuery($user: String!, $sport: Sport!) {
    so5 {
      so5Fixture(sport: $sport, type: LIVE, eventType: CLASSIC) {
        slug
        ...CurrentFixture_so5Fixture
      }
    }
    user(slug: $user) {
      slug
      trades(sport: [$sport], first: 20) {
        nodes {
          ...RecentPurchases_tokenOperationTradeInterface
        }
      }
      footballUserProfile {
        id
        clubName
        background {
          id
          pictureUrl
        }
        socialPictureFingerprint
        highlightedDeck {
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      baseballUserProfile {
        id
        clubName
        background {
          id
          pictureUrl
        }
        socialPictureFingerprint
        highlightedDeck {
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      nbaUserProfile {
        id
        clubName
        background {
          id
          pictureUrl
        }
        socialPictureFingerprint
        highlightedDeck {
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
      ...CurrentFixture_user
      ...WatchlistsBlock_user
    }
  }
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${Gallery.fragments.deck}
  ${Gallery.fragments.publicUserInfoInterface}
  ${CurrentFixture.fragments.so5Fixture}
  ${CurrentFixture.fragments.user}
  ${RecentPurchases.fragments.tokenOperationTradeInterface}
  ${WatchlistsBlock.fragments.user}
` as TypedDocumentNode<ManagerProfileQuery, ManagerProfileQueryVariables>;

const ManagerProfilePage = () => {
  const { generateSportContextPath, sport } = useSportContext();
  const { slug } = useTypedParams<PageParams>();
  const {
    flags: { campaignsWinners = [] },
  } = useFeatureFlags<{
    campaignsWinners: CampaignWinner[];
  }>();

  const [editingListSlug, setEditingListSlug] = useState<string | null>();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const galleryScrollRef = useRef<HTMLDivElement>(null);
  const { data, loading } = useQuery(MANAGER_PROFILE_QUERY, {
    variables: {
      sport,
      user: slug,
    },
    skip: !slug,
  });

  if (loading || !data) {
    return <LoadingIndicator fullHeight />;
  }

  const { user, so5 } = data;
  const { so5Fixture } = so5;
  const { footballUserProfile, baseballUserProfile, nbaUserProfile, trades } =
    user;

  const anySportUserSportProfile = {
    [Sport.FOOTBALL]: footballUserProfile,
    [Sport.BASEBALL]: baseballUserProfile,
    [Sport.NBA]: nbaUserProfile,
  }[sport];

  const shareProps = {
    managerProfilePath: generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG, {
      sport,
      params: { slug },
    }),
    managerProfileSocialPictures:
      anySportUserSportProfile?.managerProfileSocialPictureUrls,
    galleryPath: generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
      params: { slug },
    }),
    gallerySocialPictures: anySportUserSportProfile?.gallerySocialPictureUrls,
  };

  const campaignWinner = campaignsWinners.find(
    ({ represents }) => represents === data?.user.id
  );

  const filteredRecentPurchases = (trades.nodes || []).filter(
    transaction =>
      (isType(transaction, 'TokenAuction') ||
        isType(transaction, 'TokenPrimaryOffer')) &&
      !!transaction.anyCards?.length
  );

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={sport}
        userSportProfile={anySportUserSportProfile}
        {...shareProps}
      />
    ),
    currentFixture: (
      <CurrentFixture so5Fixture={so5Fixture} sport={sport} user={user} />
    ),
    recentPurchases: (
      <RecentPurchases
        userSlug={user.slug}
        transactions={filteredRecentPurchases.slice(0, 5)}
      />
    ),
    watchlists: <WatchlistsBlock user={user} />,
    gallery: (
      <>
        <Gallery
          scrollRef={galleryScrollRef}
          sport={sport}
          highlightedDeck={anySportUserSportProfile?.highlightedDeck}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={sport}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
    representedBy: campaignWinner && (
      <RepresentedBy campaignWinner={campaignWinner} />
    ),
  };

  const backgroundUrl = anySportUserSportProfile?.background?.pictureUrl;

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={backgroundUrl}>
        <Grid>
          {blocks.header}
          {blocks.currentFixture}
          {blocks.recentPurchases}
          {blocks.watchlists}
          {blocks.representedBy}
          {blocks.gallery}
        </Grid>
      </ClubLayout>
    );
  }
  return (
    <ClubLayout home bannerUrl={backgroundUrl}>
      <NewGrid>
        <Main>
          {blocks.header}
          {blocks.gallery}
        </Main>
        <Aside>
          {blocks.currentFixture}
          {blocks.recentPurchases}
          {blocks.watchlists}
          {blocks.representedBy}
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

export default ManagerProfilePage;
